import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import {
  oppositeMode,
  paletteMode,
  resolveColorMode,
  themeInMode,
  type ColorMode,
} from '@/features/storefront/colorMode/palette';
import { resolveVariant } from '@/features/storefront/layouts/families';
import { pluginEnabled, themePlugin } from '@/features/storefront/plugins/registry';
import type { StorefrontTheme } from '@/features/storefront/themeSchema';

/**
 * The shopper's light or dark mode, for a shell to wear and a toggle to flip.
 *
 * Kept in the shopper's browser, per shop, and never sent anywhere: it is how
 * they like to read, not something the shop needs to know.
 */

const STORAGE_KEY = 'storefront-color-mode';

export function colorModeStorageKey(host?: string | null): string {
  return host ? `${STORAGE_KEY}:${host}` : STORAGE_KEY;
}

export function readSavedColorMode(storageKey: string): ColorMode | null {
  try {
    const value = localStorage.getItem(storageKey);
    return value === 'light' || value === 'dark' ? value : null;
  } catch {
    return null; // storage blocked: no saved choice
  }
}

function saveColorMode(storageKey: string, mode: ColorMode): void {
  try {
    localStorage.setItem(storageKey, mode);
  } catch {
    /* the choice lasts as long as the page */
  }
}

const DARK_QUERY = '(prefers-color-scheme: dark)';

function prefersDark(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia(DARK_QUERY).matches
    : false;
}

/** The device's own setting, kept current while the page is open. */
function usePrefersDark(): boolean {
  const [dark, setDark] = useState(prefersDark);
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const query = window.matchMedia(DARK_QUERY);
    const onChange = () => setDark(query.matches);
    query.addEventListener?.('change', onChange);
    return () => query.removeEventListener?.('change', onChange);
  }, []);
  return dark;
}

export interface ColorModeState {
  /** Whether the plugin is on for this theme. */
  enabled: boolean;
  /** Whether the header shows the toggle. */
  showToggle: boolean;
  /** The mode the shopper is seeing. */
  mode: ColorMode;
  /**
   * Whether that is the other mode from the one the theme was designed in -
   * so every hex colour a merchant set on a section, a part or the promo
   * popup is to be carried across with `flipColors`/`flipHex`.
   */
  flipped: boolean;
  /** The theme document in that mode - what the shell should wear. */
  theme: StorefrontTheme | undefined;
  toggle: () => void;
}

const OFF: ColorModeState = {
  enabled: false,
  showToggle: false,
  mode: 'light',
  flipped: false,
  theme: undefined,
  toggle: () => {},
};

/**
 * The shell's half: which mode to render and the theme in it. `remember` is
 * false inside the customiser's frame, where a merchant trying the toggle must
 * not change how their own shop opens for them next time.
 */
export function useColorModeState(
  theme: StorefrontTheme | undefined,
  { storageKey, remember }: { storageKey: string; remember: boolean },
): ColorModeState {
  const [saved, setSaved] = useState(() => (remember ? readSavedColorMode(storageKey) : null));
  const dark = usePrefersDark();

  const family = theme ? resolveVariant(theme.layout, 'header') : '';
  const enabled = theme ? pluginEnabled(theme, 'color_mode', family) : false;
  const settings = themePlugin(theme, 'color_mode');
  const base = theme ? paletteMode(theme.colors) : 'light';
  const mode = enabled ? resolveColorMode(settings, base, saved, dark) : base;

  const worn = useMemo(
    () => (theme && enabled ? themeInMode(theme, mode, settings) : theme),
    // `settings` is parsed afresh each render; the theme it came from is the dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [theme, enabled, mode],
  );

  const toggle = useCallback(() => {
    const next = oppositeMode(mode);
    if (remember) saveColorMode(storageKey, next);
    setSaved(next);
  }, [mode, remember, storageKey]);

  if (!theme) return OFF;
  return { enabled, showToggle: enabled && settings.show_toggle, mode, flipped: enabled && mode !== base, theme: worn, toggle };
}

export const ColorModeContext = createContext<ColorModeState>(OFF);

/** The toggle's half: the mode on screen and the way to flip it. */
export function useColorMode(): ColorModeState {
  return useContext(ColorModeContext);
}
